/**
 * Shared AI Recommendation Upsell schema defaults (editor, preview compat, scripts).
 */
import { ensureAiRecommendationUpsellCatalogCtaButtons } from "./ai-recommendation-upsell-catalog-cta.js";
import { DEFAULT_AI_RECOMMENDATION_META, resolveAiRecommendationMaxWords, DEFAULT_AI_RECOMMENDATION_STATIC_BANNER_TEXT, AI_RECOMMENDATION_GENERATION_MODE } from "./ai-recommendation-copy.js";
import { PITCH_TYPOGRAPHY, DEFAULT_PITCH_COLORS, DEFAULT_PITCH_DISPLAY } from "./pitch-layout-display.js";
import {
  normalizeProductUpsellProductSubsections,
  PRODUCT_UPSELL_COUNTDOWN_DURATION,
  PRODUCT_UPSELL_LEGACY_CTA_TEXTS,
  PRODUCT_UPSELL_PRIMARY_CTA_TEXT,
} from "./product-upsell-recommendation-defaults.js";
import { POPUP_LAYOUT_IDS } from "./popup-layouts/registry.js";
import { ensureProductShellLayout, PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";
import { COVER_IMAGE_LAYOUT } from "./product-block-display.js";

export const AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT = PRODUCT_UPSELL_PRIMARY_CTA_TEXT;

export const AI_RECOMMENDATION_UPSELL_LEGACY_PRIMARY_CTA_TEXT =
  PRODUCT_UPSELL_LEGACY_CTA_TEXTS[0];

/** Session timer (editor label: “Fixed”) — 5:00 countdown. */
export const AI_RECOMMENDATION_UPSELL_COUNTDOWN_DURATION =
  PRODUCT_UPSELL_COUNTDOWN_DURATION;

/** Default urgency bar copy (timer digits render beside this message). */
export const AI_RECOMMENDATION_UPSELL_DEFAULT_URGENCY_TEXT =
  "Only few left — offer expires in";

export const AI_RECOMMENDATION_UPSELL_BANNER_SAMPLE_TEXT =
  DEFAULT_AI_RECOMMENDATION_STATIC_BANNER_TEXT;

const TEMPLATE_PLACEHOLDER_FONT = /^aimbrill-/i;

function isTemplatePlaceholderFontFamily(fontFamily) {
  const first = String(fontFamily || "")
    .split(",")[0]
    .trim()
    .replace(/['"]/g, "");
  return TEMPLATE_PLACEHOLDER_FONT.test(first);
}

export const AI_RECOMMENDATION_UPSELL_BANNER_CLEAR_SURFACE_CSS = Object.freeze({
  backgroundColor: "transparent",
  background: "none",
  borderStyle: "none",
  borderWidth: 0,
  borderColor: "transparent",
});

/** Pitch-layout aiBanner uses ProductRecommendationPitchLine — no line-clamp box. */
export const AI_RECOMMENDATION_UPSELL_BANNER_PITCH_LAYOUT_TEXT_CSS = Object.freeze({
  display: "block",
  overflow: "visible",
  whiteSpace: "pre-wrap",
  padding: 0,
});

/** WCAG-friendly defaults for AI pitch copy (body + label derived in preview). */
export const AI_RECOMMENDATION_UPSELL_BANNER_TYPOGRAPHY_DEFAULTS = Object.freeze({
  desktop: Object.freeze({
    fontSize: "16px",
    fontWeight: "400",
    lineHeight: "1.5",
    color: "#334155",
    textAlign: "left",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  }),
  mobile: Object.freeze({
    fontSize: "15px",
    fontWeight: "400",
    lineHeight: "1.45",
    color: "#334155",
    textAlign: "left",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  }),
});

function parseFontSizePx(value, fallback = 16) {
  const match = String(value || "").match(/([\d.]+)/);
  const n = match ? Number(match[1]) : NaN;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/**
 * Resolved typography for aiBanner TEXT (editor + preview).
 * @param {object | null | undefined} section
 * @param {"desktop"|"mobile"} [viewKey]
 */
export function resolveAiBannerTypography(section, viewKey = "desktop") {
  const desktopCss = section?.setting?.customCSS?.desktop || {};
  const viewCss = section?.setting?.customCSS?.[viewKey] || {};
  const defaults =
    AI_RECOMMENDATION_UPSELL_BANNER_TYPOGRAPHY_DEFAULTS[viewKey] ||
    AI_RECOMMENDATION_UPSELL_BANNER_TYPOGRAPHY_DEFAULTS.desktop;
  const merged =
    viewKey === "mobile"
      ? { ...defaults, ...desktopCss, ...viewCss }
      : { ...defaults, ...desktopCss, ...viewCss };

  return {
    fontFamily: merged.fontFamily || null,
    fontSize: merged.fontSize || defaults.fontSize,
    fontWeight: merged.fontWeight || defaults.fontWeight,
    lineHeight: merged.lineHeight || defaults.lineHeight,
    color: merged.color || defaults.color,
    textAlign: merged.textAlign || defaults.textAlign,
  };
}

/** Eyebrow label size scales from pitch body font size. */
export function resolveAiBannerLabelFontSize(bodyFontSize) {
  const px = parseFontSizePx(bodyFontSize, 16);
  return `${Math.max(10, Math.min(14, Math.round(px * 0.7)))}px`;
}

function stripLegacyDraggableChromeCss(css = {}) {
  const {
    border: _border,
    borderWidth: _borderWidth,
    borderStyle: _borderStyle,
    borderColor: _borderColor,
    borderTop: _borderTop,
    borderRight: _borderRight,
    borderBottom: _borderBottom,
    borderLeft: _borderLeft,
    boxShadow: _boxShadow,
    outline: _outline,
    background: _background,
    backgroundColor: _backgroundColor,
    padding: _padding,
    paddingTop: _paddingTop,
    paddingRight: _paddingRight,
    paddingBottom: _paddingBottom,
    paddingLeft: _paddingLeft,
    ...rest
  } = css;
  return rest;
}

function mergeAiBannerViewCss(view, existing = {}, { pitchLayout = false } = {}) {
  if (pitchLayout) {
    const {
      WebkitLineClamp: _clamp,
      WebkitBoxOrient: _orient,
      textOverflow: _ellipsis,
      ...restWithoutClamp
    } = existing;
    const rest = stripLegacyDraggableChromeCss(restWithoutClamp);
    const hidden = rest.display === "none";

    return {
      ...AI_RECOMMENDATION_UPSELL_BANNER_CLEAR_SURFACE_CSS,
      fontWeight: "600",
      lineHeight: "1.35",
      fontSize: PITCH_TYPOGRAPHY.bodyFontSize,
      color: AI_RECOMMENDATION_UPSELL_BANNER_TYPOGRAPHY_DEFAULTS.desktop.color,
      ...rest,
      ...AI_RECOMMENDATION_UPSELL_BANNER_CLEAR_SURFACE_CSS,
      ...AI_RECOMMENDATION_UPSELL_BANNER_PITCH_LAYOUT_TEXT_CSS,
      ...(hidden ? { display: "none" } : {}),
    };
  }

  const defaults =
    AI_RECOMMENDATION_UPSELL_BANNER_TYPOGRAPHY_DEFAULTS[view] ||
    AI_RECOMMENDATION_UPSELL_BANNER_TYPOGRAPHY_DEFAULTS.desktop;
  const existingPx = parseFontSizePx(existing.fontSize, 0);
  const needsReadableSize = !existing.fontSize || existingPx < 15;
  const hidden = existing.display === "none";

  return {
    ...defaults,
    ...existing,
    ...(needsReadableSize ? { fontSize: defaults.fontSize } : {}),
    ...(!existing.color ? { color: defaults.color } : {}),
    ...(!existing.lineHeight ? { lineHeight: defaults.lineHeight } : {}),
    ...(!existing.fontWeight ? { fontWeight: defaults.fontWeight } : {}),
    ...(!existing.textAlign ? { textAlign: defaults.textAlign } : {}),
    ...AI_RECOMMENDATION_UPSELL_BANNER_CLEAR_SURFACE_CSS,
    ...(hidden ? { display: "none" } : {}),
  };
}

/**
 * @param {object | null | undefined} section aiBanner TEXT section
 * @param {{ bodyFont?: string, secondaryFont?: string, textColor?: string }} [themeHints]
 */
export function applyAiRecommendationUpsellBannerDefaults(section, themeHints = {}) {
  if (!section) return;
  section.setting = section.setting || {};
  section.setting.customCSS = section.setting.customCSS || {};
  const secondaryFont = themeHints.secondaryFont || themeHints.bodyFont || "";
  const pitchLayout = section?.setting?.pitchLayout === true;

  for (const view of ["desktop", "mobile"]) {
    const existing = section.setting.customCSS[view] || {};
    const merged = mergeAiBannerViewCss(view, existing, { pitchLayout });
    if (secondaryFont) {
      if (!existing.fontFamily || isTemplatePlaceholderFontFamily(existing.fontFamily)) {
        merged.fontFamily = secondaryFont;
      }
    } else if (isTemplatePlaceholderFontFamily(existing.fontFamily)) {
      delete merged.fontFamily;
    }
    if (themeHints.textColor && !existing.color) {
      merged.color = themeHints.textColor;
    }
    section.setting.customCSS[view] = merged;
  }

  const raw = String(section.text || "").trim();
  if (!raw || raw === "<p></p>") {
    section.text = AI_RECOMMENDATION_UPSELL_BANNER_SAMPLE_TEXT;
  }
}

/**
 * @param {object | null | undefined} product PRODUCT block
 */
export function normalizeAiRecommendationUpsellProductSubsections(product, popup) {
  normalizeProductUpsellProductSubsections(product, { catalogAlign: true });
  if (!product) return;

  product.setting = product.setting || {};
  product.setting.layoutFlow = true;
  const layout = product.setting.layout || {};
  product.setting.layout = {
    ...layout,
    preset: POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL,
    imagePosition: layout.imagePosition === "top" ? "top" : "left",
    quantityPosition: layout.quantityPosition === "right" ? "right" : "left",
    density: layout.density || "standard",
  };
  if (popup) {
    ensureProductShellLayout(product, popup);
  }

  // Single full-bleed cover (no strip / hero+thumbs) — matches product-page hover slot.
  if (Array.isArray(product.sections)) {
    for (const sub of product.sections) {
      if (sub?.type !== "COVER_IMAGE") continue;
      sub.objectFit = "cover";
      sub.setting = sub.setting || {};
      const prev = sub.setting.display || {};
      sub.setting.display = {
        ...prev,
        layout: COVER_IMAGE_LAYOUT.SINGLE,
        autoImageHeight: false,
        sizes: {
          desktop: {
            ...(prev.sizes?.desktop || {}),
            columnWidth: 140,
            mainHeight: 140,
            thumbWidth: 40,
            thumbHeight: 40,
          },
          mobile: {
            ...(prev.sizes?.mobile || {}),
            columnWidth: 112,
            mainHeight: 112,
            thumbWidth: 36,
            thumbHeight: 36,
          },
        },
      };
    }
  }
}

function resolvePrimaryCtaText(text, fallback = AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT) {
  const trimmed = String(text ?? "").trim();
  if (
    !trimmed ||
    trimmed === AI_RECOMMENDATION_UPSELL_LEGACY_PRIMARY_CTA_TEXT
  ) {
    return fallback;
  }
  return trimmed;
}

/**
 * @param {object[] | null | undefined} sections
 * @param {string} [buttonText]
 */
export function applyAiRecommendationUpsellPrimaryCtaDefaults(
  sections,
  buttonText = AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT
) {
  if (!Array.isArray(sections)) return;
  const buttons = sections.filter((s) => s?.type === "BUTTON");
  const primary =
    buttons.find((s) => s?.setting?.role === "primaryCta") || buttons[0];
  if (!primary) return;
  primary.text = resolvePrimaryCtaText(primary.text, buttonText);
}

/**
 * @param {object | null | undefined} generalSettings
 */
export function normalizeAiRecommendationUpsellLayoutOptions(generalSettings) {
  if (!generalSettings || typeof generalSettings !== "object") return;

  generalSettings.productShellLayout = PRODUCT_SHELL_LAYOUT.HORIZONTAL;

  generalSettings.layoutConfig = generalSettings.layoutConfig || { version: 1 };
  const options = {
    ...(generalSettings.layoutConfig.options || {}),
  };

  options.buttonText = resolvePrimaryCtaText(
    options.buttonText,
    AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT
  );

  generalSettings.layoutConfig = {
    version: generalSettings.layoutConfig.version ?? 1,
    options,
  };
}

/**
 * Normalize one step's top-level sections for AI Recommendation Upsell.
 * @param {object[] | null | undefined} sections
 */
export function normalizeAiRecommendationUpsellSections(
  sections,
  {
    buttonText = AI_RECOMMENDATION_UPSELL_PRIMARY_CTA_TEXT,
    themeHints = {},
    popup = null,
  } = {}
) {
  if (!Array.isArray(sections)) return;

  const aiBanner = sections.find(
    (s) => s?.type === "TEXT" && s?.setting?.role === "aiBanner"
  );
  if (aiBanner) {
    aiBanner.setting = aiBanner.setting || {};
    if (aiBanner.setting.pitchLayout !== false) {
      aiBanner.setting.pitchLayout = true;
      const display = aiBanner.setting.display || {};
      aiBanner.setting.display = {
        ...DEFAULT_PITCH_DISPLAY,
        ...display,
        layout: display.layout || DEFAULT_PITCH_DISPLAY.layout,
        showDivider: false,
        colors: {
          ...DEFAULT_PITCH_COLORS,
          ...(display.colors || {}),
        },
      };
    }
    const mergedMeta = { ...(aiBanner.aiMeta || {}) };
    const resolvedMode = [
      AI_RECOMMENDATION_GENERATION_MODE.STATIC,
      AI_RECOMMENDATION_GENERATION_MODE.FALLBACK,
      AI_RECOMMENDATION_GENERATION_MODE.RUNTIME,
    ].includes(mergedMeta.generationMode)
      ? mergedMeta.generationMode
      : AI_RECOMMENDATION_GENERATION_MODE.STATIC;
    aiBanner.aiMeta = {
      ...DEFAULT_AI_RECOMMENDATION_META,
      ...mergedMeta,
      useAiGeneration: mergedMeta.useAiGeneration === true,
      generationMode:
        mergedMeta.useAiGeneration === true
          ? resolvedMode === AI_RECOMMENDATION_GENERATION_MODE.RUNTIME
            ? AI_RECOMMENDATION_GENERATION_MODE.RUNTIME
            : AI_RECOMMENDATION_GENERATION_MODE.FALLBACK
          : AI_RECOMMENDATION_GENERATION_MODE.STATIC,
      maxWords: resolveAiRecommendationMaxWords({
        prompt: mergedMeta.prompt,
        maxWords: mergedMeta.maxWords,
      }),
    };
    applyAiRecommendationUpsellBannerDefaults(aiBanner, themeHints);
  }

  ensureAiRecommendationUpsellCatalogCtaButtons(sections);
  applyAiRecommendationUpsellPrimaryCtaDefaults(sections, buttonText);

  const product = sections.find((s) => s?.type === "PRODUCT");
  normalizeAiRecommendationUpsellProductSubsections(product, popup);
}

function hasSavedSettingValue(value) {
  return value != null && value !== "";
}

function pickSavedSettingValue(saved, defaultValue) {
  return hasSavedSettingValue(saved) ? saved : defaultValue;
}

function pickSavedBoolean(saved, defaultValue) {
  return typeof saved === "boolean" ? saved : defaultValue;
}

function pickSavedNumber(saved, defaultValue) {
  if (!hasSavedSettingValue(saved)) return defaultValue;
  const parsed = Number(saved);
  return Number.isFinite(parsed) ? parsed : defaultValue;
}

/**
 * AI Recommendation Upsell condition defaults for new templates only.
 * Saved popup/API values always win when present.
 * @param {object | null | undefined} generalSettings
 */
export function normalizeAiRecommendationUpsellConditions(generalSettings) {
  if (!generalSettings || typeof generalSettings !== "object") return;

  generalSettings.condition = generalSettings.condition || {};
  const condition = generalSettings.condition;
  const existingPages = condition.pages || {};
  const existingTrigger = condition.trigger || {};
  const existingProduct = condition.product || {};
  const ecommerce = condition.ecommerce || {};

  condition.pages = {
    ...existingPages,
    all_pages: pickSavedBoolean(existingPages.all_pages, true),
    show_pages: pickSavedBoolean(existingPages.show_pages, false),
  };

  condition.trigger = {
    ...existingTrigger,
    allow_show_after: pickSavedBoolean(existingTrigger.allow_show_after, true),
    show_after: pickSavedNumber(existingTrigger.show_after, 3),
    ui_delay_unit: pickSavedSettingValue(existingTrigger.ui_delay_unit, "seconds"),
    allow_after_scroll: pickSavedBoolean(existingTrigger.allow_after_scroll, false),
    allow_Exit_Intent: pickSavedBoolean(existingTrigger.allow_Exit_Intent, false),
    allow_after_inactive: pickSavedBoolean(existingTrigger.allow_after_inactive, false),
    allow_onclick: pickSavedBoolean(existingTrigger.allow_onclick, false),
    allow_onhover: pickSavedBoolean(existingTrigger.allow_onhover, false),
    show_after_scroll: pickSavedSettingValue(existingTrigger.show_after_scroll, "50"),
    show_after_scroll_unit: pickSavedSettingValue(
      existingTrigger.show_after_scroll_unit,
      "PERCENT"
    ),
    show_after_inactive: pickSavedNumber(existingTrigger.show_after_inactive, 10),
    allow_close_after_time: pickSavedBoolean(
      existingTrigger.allow_close_after_time,
      false
    ),
    close_after: pickSavedNumber(existingTrigger.close_after, 30),
    step: pickSavedNumber(existingTrigger.step, 0),
    show_popup: pickSavedSettingValue(existingTrigger.show_popup, "every_time"),
  };

  condition.hasHideProductTriggerSetting = pickSavedBoolean(
    condition.hasHideProductTriggerSetting,
    true
  );

  const rules = Array.isArray(ecommerce.rules) ? ecommerce.rules : [];
  const filteredRules = rules.filter(
    (rule) => !String(rule?.field || "").startsWith("product_trigger_")
  );
  condition.ecommerce = {
    ...ecommerce,
    rules: filteredRules,
    enabled: pickSavedBoolean(ecommerce.enabled, filteredRules.length > 0),
  };

  const existingOffer = existingProduct.products_to_offer;
  const hasOfferType =
    existingOffer &&
    typeof existingOffer === "object" &&
    typeof existingOffer.type === "string" &&
    existingOffer.type.trim() !== "";

  condition.product = {
    ...existingProduct,
    onCartAdd: pickSavedBoolean(existingProduct.onCartAdd, false),
    // Always one offer product for AI Recommendation Upsell.
    products_to_offer_limit: 1,
    // Template/API may ship products_to_offer: null while the Offer UI shows
    // "Recommended by Weupsell AI" as a display-only fallback. Persist the real
    // default so live hydrate can resolve products without a re-select.
    products_to_offer: hasOfferType
      ? existingOffer
      : {
          type: "shopify_recommended",
          value: Array.isArray(existingOffer?.value) ? existingOffer.value : [],
        },
  };
  if (condition.product.offer_is_triggered) {
    delete condition.product.offer_is_triggered;
  }
}
